import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import Logo from "./Logo";
import { trackEvent, openCookiePreferences } from "@/lib/tracking";

export default function Footer() {
  const { pathname } = useLocation();
  return (
    <footer data-testid="site-footer" className="bg-[#07111F] text-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-10 sm:pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
        <div className="sm:col-span-2 lg:col-span-5">
          <Logo variant="dark" />
          <p className="mt-5 text-sm text-white/65 leading-relaxed max-w-md">
            Xadentra helps B2B teams build scalable marketing automation, campaign operations, CRM
            alignment, and reporting systems that turn marketing execution into measurable pipeline impact.
          </p>
          <div className="mt-8 flex gap-3">
            <a
              href="https://www.linkedin.com/company/xadentra/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="footer-social-linkedin"
              className="w-10 h-10 grid place-items-center border border-white/15 rounded-lg hover:border-[#00D4FF] hover:text-[#00D4FF] transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mb-5">Services</h3>
          <ul className="space-y-3 text-sm text-white/80">
            <li><Link to="/marketo-services" className="hover:text-[#00D4FF]">Adobe Marketo Engage</Link></li>
            <li><Link to="/services/salesforce-crm" className="hover:text-[#00D4FF]">Salesforce &amp; CRM</Link></li>
            <li><Link to="/services/campaign-operations" className="hover:text-[#00D4FF]">Campaign Ops</Link></li>
            <li><Link to="/services/managed-services" className="hover:text-[#00D4FF]">Managed Services</Link></li>
            <li><Link to="/services/martech-integration" className="hover:text-[#00D4FF]">Martech &amp; Data</Link></li>
            <li><Link to="/services/reporting-attribution" className="hover:text-[#00D4FF]">Reporting</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mb-5">Company</h3>
          <ul className="space-y-3 text-sm text-white/80">
            <li><Link to="/about" className="hover:text-[#00D4FF]">About</Link></li>
            <li><Link to="/case-studies" className="hover:text-[#00D4FF]">Case Studies</Link></li>
            <li><Link to="/resources" className="hover:text-[#00D4FF]">Resources</Link></li>
            <li><Link to="/contact" className="hover:text-[#00D4FF]">Contact</Link></li>
          </ul>

          <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mt-8 mb-5">Legal</h3>
          <ul className="space-y-3 text-sm text-white/80">
            <li><Link to="/privacy-policy" className="hover:text-[#00D4FF]">Privacy Policy</Link></li>
            <li><Link to="/cookie-policy" className="hover:text-[#00D4FF]">Cookie Policy</Link></li>
            <li><Link to="/terms" className="hover:text-[#00D4FF]">Terms of Use</Link></li>
            <li>
              <button
                type="button"
                onClick={() => {
                  trackEvent("cta_click", { id: "footer_cookie_prefs" });
                  openCookiePreferences();
                }}
                data-testid="footer-cookie-prefs"
                className="hover:text-[#00D4FF] text-left"
              >
                Cookie Preferences
              </button>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mb-5">Get in touch</h3>
          <a href={"mail" + "to:" + "contact" + "@" + "xadentra.com"} className="text-sm block hover:text-[#00D4FF] break-all" data-testid="footer-email">
            {"contact" + "@" + "xadentra.com"}
          </a>
          <Link
            to="/contact#contact-form"
            data-testid="footer-cta"
            onClick={(e) => {
              trackEvent("cta_click", { id: "footer_send_message" });
              if (pathname === "/contact") {
                e.preventDefault();
                document
                  .getElementById("contact-form")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            className="xa-btn xa-btn-ghost-dark mt-6 text-sm"
          >
            Send us a message <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/45">
          <div>© {new Date().getFullYear()} Xadentra. Marketing automation built to move pipeline.</div>
          <div className="max-w-xl md:text-right">
            Adobe Marketo Engage and Salesforce are trademarks of their respective owners. Xadentra
            is not affiliated with or endorsed by these companies unless otherwise stated.
          </div>
        </div>
      </div>
    </footer>
  );
}
